import { useMemo } from 'react';
import useSWR from 'swr';
import axios from 'axios';

import { Modal } from '@shared/components/modals/Modal';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { CommonButtonWrapper } from '@styles/CommonStyles';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { queryParams } from '@shared/commonFunctions';
import { SERVICES } from '@shared/constants/apiConfig';

interface IPricing {
    term: string;
    rate: number;
}

interface ICulsCudlConfirmationModalProps {
    open: boolean;
    loading: boolean;
    resetPosition?: number;
    onClose: () => void;
    onConfirm: () => void;
}

const columns = [
    {
        field: 'term',
        headerName: 'Term',
        flex: 1,
    },
    {
        field: 'rate',
        headerName: 'Rate',
        flex: 1,
    },
];

export const CulsCudlConfirmationModal = ({
    open,
    loading,
    resetPosition,
    onClose,
    onConfirm,
}: ICulsCudlConfirmationModalProps) => {
    const params = queryParams();
    const leadId = params.leadId;
    const dealId = params.dealId;
    const pricingURL = useMemo(() => {
        if (!dealId || !leadId || !open) {
            return null;
        }

        return `${SERVICES.LENDER}/culs/pricing/getrates?dealId=${dealId}&leadId=${leadId}`;
    }, [dealId, leadId, open]);

    const {
        data: pricingResponse,
        isLoading: pricingLoading,
    } = useSWR(
        pricingURL ? [pricingURL] : null,
        ([url]) => axios.post<IPricing[]>(url),
        {
            revalidateIfStale: true,
        }
    );

    const pricingRows = useMemo(
        () =>
            (pricingResponse?.data ?? []).map((item: IPricing) => ({
                term: item.term,
                rate: `${item.rate}%`,
            })),
        [pricingResponse]
    );

    const handleClose = () => {
        if (!loading && !pricingLoading) {
            onClose();
        }
    };

    return (
        <Modal
            open={open}
            title="Are you sure you want to push CUDL?"
            dialogWidth="600px"
            resetPosition={resetPosition}
            isSubmitting={loading || pricingLoading}
            onClose={handleClose}
        >
            <PhoenixGrid
                noButton
                withHeader
                autoHeight
                hideFooter
                canNotEdit
                canNotCopy
                disableSelectionOnClick
                rows={pricingRows}
                columns={columns}
                loading={pricingLoading}
                getRowId={(row) => row.term}
            />

            <CommonButtonWrapper>
                <CancelButton
                    disabled={loading || pricingLoading}
                    onClick={handleClose}
                />

                <SaveButton
                    label="Push CUDL"
                    disabled={loading || pricingLoading}
                    isLoading={loading}
                    onClick={onConfirm}
                />
            </CommonButtonWrapper>
        </Modal>
    );
};
